import type { BoxProps } from '@hover/blueprint';
import { Box, motion } from '@hover/blueprint';
import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useHistory } from 'react-router';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { useStickHeaderShadowOpacity } from 'src/modules/homeownerApp/hooks/scroll';
import { useLocation } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';
import { hideScrollbarsStyles } from 'src/shared/utils/styles';

import type { NavItemSchema } from '../../Navbar';
import { NavbarButton } from '../../Navbar/NavbarButton';

interface ProjectDetailsNavigationProps extends BoxProps {
  onNavButtonClick: (sectionName: string) => void;
  projectId: number;
  navRef: React.RefObject<HTMLDivElement>;
  recentlyViewedSection?: string;
  availableNavLinks: NavItemSchema[];
}

const MotionBox = motion(Box);

const ProjectDetailsNavigation = ({
  onNavButtonClick,
  projectId,
  px,
  navRef,
  recentlyViewedSection,
  availableNavLinks,
}: ProjectDetailsNavigationProps) => {
  const location = useLocation();
  const history = useHistory();
  const { trackingData } = useHomeownerAppContext();
  const opacity = useStickHeaderShadowOpacity(navRef);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const handleScrollEnd = () => {
      setIsScrolling(false);
    };

    window.addEventListener('scrollend', handleScrollEnd);

    return () => {
      window.removeEventListener('scrollend', handleScrollEnd);
    };
  }, []);

  const [selectedNavButton, setSelectedNavButton] = useState<
    string | undefined
  >(location.hash.slice(1) || availableNavLinks[0]?.name);

  useEffect(() => {
    if (recentlyViewedSection && !isScrolling) {
      setSelectedNavButton(recentlyViewedSection);
    }
    // Only trigger this when the recentlyViewedSection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentlyViewedSection]);

  const handleNavButtonClick = ({
    path,
    name: navName,
    trackId,
  }: NavItemSchema) => {
    if (selectedNavButton !== navName) {
      flushSync(() => setIsScrolling(true));
    }
    setSelectedNavButton(navName);
    history.push(path);
    buttonPressed({
      ...trackingData,
      button_text: navName,
      button_location: 'Project Jump List',
      primary_cta: false,
      project_id: projectId,
      track_id: trackId,
    });
    if (typeof onNavButtonClick === 'function') {
      onNavButtonClick(navName);
    }
  };

  return (
    <>
      {!!availableNavLinks.length && (
        <MotionBox
          alignItems={{ base: 'flex-start', tablet: 'center' }}
          as="nav"
          background="neutral.0"
          data-testid="project-details-navigation"
          flexDir="column"
          minW="100%"
          my="-200" // Negative margin to offset the gap between the navbar and the contents
          pb={{ base: 300, tablet: 600 }}
          position="sticky"
          pt={{ base: 300, tablet: 400 }}
          ref={navRef}
          top={0}
          width="100%"
          zIndex="sticky"
        >
          <MotionBox
            bottom="0"
            left="0"
            position="absolute"
            right="0"
            shadow="distance400"
            style={{ opacity }}
            top="0"
          />
          <Box
            overflowX="auto"
            position="relative"
            px={px}
            sx={hideScrollbarsStyles()}
            width="100%"
          >
            <Box
              alignItems="flex-start"
              data-testid="project-details-navigation-buttons"
              gap={600}
              justifyContent="center"
              mx="auto"
              pt={400}
              width="fit-content"
            >
              {availableNavLinks.map(link => (
                <NavbarButton
                  capitalize={false}
                  color={
                    selectedNavButton === link.name
                      ? 'text.primary'
                      : 'text.secondary'
                  }
                  isSelected={link.name === selectedNavButton}
                  key={link.name}
                  mode="light"
                  navbarItem={link}
                  onClick={() => handleNavButtonClick(link)}
                  padding={0}
                  translatedName={link.translatedName}
                  whiteSpace="nowrap"
                />
              ))}
            </Box>
          </Box>
        </MotionBox>
      )}
    </>
  );
};

export { ProjectDetailsNavigation };
